/**
 * Layout pour les pages /platform/* (mode plateforme One For All Distribution cross-tenant).
 *
 * Garde : accessible uniquement aux users avec isPlatformAdmin === true.
 * Si quelqu'un d'autre tape /platform, il est redirigé vers /dashboard.
 *
 * UX : sidebar (Collectivités / Audit log) + topbar URGENT (rouge) pour marquer
 * visuellement qu'on est dans une zone privilégiée (différent du bleu tech
 * du backoffice mairie).
 *
 * Refonte R3 (mai 2026) :
 *   - Topbar en glass avec halo radial urgent localisé + gradient subtil
 *   - LogoMark custom (Radio + gradient urgent) — accent "platform" qui n'existe
 *     pas en module officiel, donc on garde un style ciblé inline.
 *   - NavLink en pill glass active (accent urgent)
 */

import { NavLink, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/lib/auth-context';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SectionEyebrow, Button } from '@/components/ui';
import { ArrowLeft, Building2, FileText, Radio } from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Collectivités', to: '/platform/collectivites', icon: Building2 },
  { label: 'Audit plateforme', to: '/platform/audit-log', icon: FileText },
] as const;

export function PlatformLayout() {
  usePageTitle('Plateforme');
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div
          className="rounded-r-2xl border border-border bg-surface/55 px-6 py-5 backdrop-blur-glass shadow-glass flex items-center gap-3"
          role="status"
          aria-label="Chargement"
        >
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-urgent border-t-transparent" aria-hidden="true" />
          <span className="text-sm text-ink-muted">Chargement de la plateforme…</span>
        </div>
      </div>
    );
  }
  if (!user || !user.isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen text-ink">
      {/* ===== Topbar glass avec halo urgent ===== */}
      {/* Le rouge signale visuellement qu'on est dans une zone privilégiée (cross-tenant). */}
      <header
        className="sticky top-0 z-20 flex items-center gap-3 border-b border-urgent/30 px-4 py-3 backdrop-blur-glass sm:px-6"
        role="banner"
        style={{
          backgroundImage:
            'radial-gradient(280px 100px at 10% 50%, oklch(var(--c-urgent) / 0.28), transparent 70%), ' +
            'linear-gradient(90deg, oklch(var(--c-urgent) / 0.10), oklch(var(--c-bg) / 0.40))',
        }}
      >
        {/* LogoMark custom (pas dans Module officiel) — Radio sur gradient urgent */}
        <div
          className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-gradient-to-b from-urgent to-urgent/70 text-white shadow-glow-urgent"
          style={{ boxShadow: '0 6px 20px -8px oklch(var(--c-urgent) / 0.55), inset 0 1px 0 oklch(1 0 0 / 0.25)' }}
          aria-hidden="true"
        >
          <Radio className="h-4 w-4" aria-hidden="true" />
        </div>

        <div className="min-w-0">
          <SectionEyebrow className="text-urgent/80">Plateforme One For All Distribution</SectionEyebrow>
          <div className="font-display text-sm font-semibold tracking-r-snug text-ink truncate">
            Mode supervision cross-tenant
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {user && (
            <span className="hidden md:inline text-xs text-ink-subtle">
              {user.prenom} {user.nom} · Super admin
            </span>
          )}
          <Button variant="danger" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Sortir</span>
          </Button>
          <ThemeToggle />
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-6 px-4 py-6 sm:px-6">
        {/* ===== Sidebar desktop : NavLinks en pills glass urgent ===== */}
        <aside className="hidden w-56 shrink-0 lg:block" aria-label="Navigation plateforme">
          <nav className="sticky top-24 space-y-1.5">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  [
                    'flex h-11 items-center gap-3 rounded-r-lg px-3.5 text-sm font-medium transition-all',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-urgent focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                    isActive
                      ? 'border border-urgent/30 bg-urgent-tint/70 text-urgent shadow-[0_0_0_4px_oklch(var(--c-urgent)/0.08)]'
                      : 'border border-transparent text-ink-muted hover:bg-surface/60 hover:text-ink hover:border-border',
                  ].join(' ')
                }
              >
                <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* ===== Sidebar mobile : nav horizontale scrollable ===== */}
        <nav
          className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 lg:hidden"
          aria-label="Navigation plateforme (mobile)"
        >
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  'inline-flex h-10 shrink-0 items-center gap-2 rounded-r-pill border px-3.5 text-sm font-medium backdrop-blur-glass transition-all',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-urgent focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                  isActive
                    ? 'border-urgent/30 bg-urgent-tint/70 text-urgent'
                    : 'border-border bg-surface/40 text-ink-muted hover:text-ink hover:border-border-strong',
                ].join(' ')
              }
            >
              <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <main id="main-content" className="min-w-0 flex-1 animate-fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
